import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Loader2, Lock } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      {label}
    </li>
  );
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const checks = useMemo(() => ({
    length: next.length >= 8,
    upper: /[A-Z]/.test(next),
    lower: /[a-z]/.test(next),
    digit: /\d/.test(next),
    match: next.length > 0 && next === confirm,
    different: next !== current && next.length > 0,
  }), [next, confirm, current]);
  const ok = Object.values(checks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      await refresh();
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Account</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
          My Profile
        </h1>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <div className="font-medium text-slate-900">{user?.first_name} {user?.last_name}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <div className="font-mono text-sm text-slate-800">{user?.email}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
            <div className="font-medium text-slate-800">{user?.role?.role_name || "—"}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Expertise filter</Label>
            <div className="text-slate-800">{user?.expertise_filter}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-5 h-5 text-slate-500" />
          <div className="text-lg font-semibold text-slate-900">Change password</div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="profile-change-password-form">
          <div>
            <Label>Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="profile-current-password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>New password</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="profile-new-password" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="profile-confirm-password" />
            </div>
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 bg-slate-50 border border-slate-200 rounded-md p-3">
            <Rule ok={checks.length} label="At least 8 characters" />
            <Rule ok={checks.upper} label="One uppercase letter" />
            <Rule ok={checks.lower} label="One lowercase letter" />
            <Rule ok={checks.digit} label="One number" />
            <Rule ok={checks.match} label="Both passwords match" />
            <Rule ok={checks.different} label="Different from current" />
          </ul>
          <div className="flex justify-end">
            <Button type="submit" disabled={!ok || saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="profile-save-password-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
